import type { SocietyLayers, SocietyMode } from "../model/types";

export const SOCIETY_PRESET_PARAM = "preset";
export const SOCIETY_ENTRY_PARAM = "from";
export const SOCIETY_SIMULATION_ENTRY = "simulation";
export const SOCIETY_HISTORY_PARAM = "history";
export const SOCIETY_DATA_SOURCE_PARAM = "source";
export const SOCIETY_DEMO_DATA_SOURCE = "demo";
export const SOCIETY_SAMPLE_SIZES = [
  30_000,
  100_000,
  300_000,
  1_000_000,
] as const;
export const SOCIETY_KOL_SIZES = [5, 10, 20, 50, 100] as const;

export type SocietyPresetId =
  | "milk-tea-launch"
  | "boutique-coffee"
  | "beauty-seeding"
  | "authentic-review";

export interface SocietyPreset {
  id: SocietyPresetId;
  nameKey:
    | "prediction.examples.milkTea"
    | "prediction.examples.coffee"
    | "prediction.examples.beauty"
    | "prediction.examples.review";
  sampleSize: (typeof SOCIETY_SAMPLE_SIZES)[number];
  kolCount: (typeof SOCIETY_KOL_SIZES)[number];
  mode: SocietyMode;
  layers: SocietyLayers;
  speed: number;
}

/**
 * 工作台四个案例对应的星图预设。各预设只改变前端初始视图，
 * 不修改星图接口数据或预测完成后的真实星图逻辑。
 */
export const SOCIETY_PRESETS = [
  {
    id: "milk-tea-launch",
    nameKey: "prediction.examples.milkTea",
    sampleSize: 100_000,
    kolCount: 20,
    mode: "tier",
    layers: {
      halo: false,
      stars: true,
      kols: true,
      links: true,
      meteors: true,
      kolRings: true,
      souls: false,
      pulses: true,
      tierLinks: true,
    },
    speed: 1.4,
  },
  {
    id: "boutique-coffee",
    nameKey: "prediction.examples.coffee",
    sampleSize: 30_000,
    kolCount: 5,
    mode: "tier",
    layers: {
      halo: false,
      stars: true,
      kols: true,
      links: true,
      meteors: true,
      kolRings: true,
      souls: true,
      pulses: false,
      tierLinks: true,
    },
    speed: 0.8,
  },
  {
    id: "beauty-seeding",
    nameKey: "prediction.examples.beauty",
    sampleSize: 300_000,
    kolCount: 50,
    mode: "verdict",
    layers: {
      halo: true,
      stars: true,
      kols: true,
      links: false,
      meteors: true,
      kolRings: true,
      souls: true,
      pulses: true,
      tierLinks: false,
    },
    speed: 1.1,
  },
  {
    id: "authentic-review",
    nameKey: "prediction.examples.review",
    sampleSize: 100_000,
    kolCount: 10,
    mode: "verdict",
    layers: {
      halo: false,
      stars: true,
      kols: false,
      links: false,
      meteors: false,
      kolRings: false,
      souls: true,
      pulses: false,
      tierLinks: false,
    },
    speed: 0.6,
  },
] as const satisfies readonly SocietyPreset[];

export function getSocietyPreset(id: string | null) {
  return SOCIETY_PRESETS.find((preset) => preset.id === id);
}

export function getSocietyPresetPath(id: SocietyPresetId) {
  return `/society?${SOCIETY_PRESET_PARAM}=${encodeURIComponent(id)}`;
}

export function getSocietyDemoPresetPath(id: SocietyPresetId) {
  const query = new URLSearchParams({
    [SOCIETY_PRESET_PARAM]: id,
    [SOCIETY_DATA_SOURCE_PARAM]: SOCIETY_DEMO_DATA_SOURCE,
  });
  return `/society?${query.toString()}`;
}

export function getSocietyHistoryPath(id: string) {
  return `/society?${SOCIETY_HISTORY_PARAM}=${encodeURIComponent(id)}`;
}

export function getSocietySimulationPath(historyId?: string | null) {
  const query = new URLSearchParams({ [SOCIETY_ENTRY_PARAM]: SOCIETY_SIMULATION_ENTRY });
  if (historyId) query.set(SOCIETY_HISTORY_PARAM, historyId);
  return `/society?${query.toString()}`;
}
